import React, { forwardRef } from 'react'
import ReactInputMask from 'react-input-mask'
import styled, { css } from 'styled-components'
import { colors } from '../../styles/constants'

export const Label = styled.label`
  position: absolute;
  z-index: 1;
  top: -12px;
  left: 14px;
  margin: 0px;
  background-color: ${colors.white};
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  display: flex;
  align-items: center;
  color: ${colors.gray.dark};
  padding: 4px;
  transition: all 0.2s ease;
  pointer-events: none;
`

export const Container = styled.div`
  position:relative;
  width: 100%;
  max-width: ${props => props.maxWidth ? props.maxWidth : '100%'};
  margin-bottom: ${props => props.marginBottom ? props.marginBottom : '20px'};

  ::-webkit-input-placeholder { /* Edge */
    color: ${colors.gray.light};
    font-size: 14px;
  }

  :-ms-input-placeholder { /* Internet Explorer 10-11 */
    color: ${colors.gray.light};
    font-size: 14px;
  }

  ::placeholder {
    color: ${colors.gray.light};
    font-size: 14px;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active{
    box-shadow: 0 0 0 30px white inset !important;
    -webkit-box-shadow: 0 0 0 30px white inset !important;
  }

  input[type='date'], input[type='time'], input[type='datetime-local'] {
    -webkit-appearance: none;
    appearance: none;
  }

  ${({ hasError }) => hasError && css`
    ${Label} {
      color: #C80E3B;
    }
    ${Icon} {
      path {
          fill: #C80E3B;
      }
    }
  `}
`

const Max = styled.span`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: ${colors.gray.medium};
  margin-right: 16px;
  display: flex;
  justify-content: flex-end;
`

export const InputStyled = styled.input`
  width: 100%;
  height: ${({ height }) => height || '56px'};
  ${({ lineHeight }) => lineHeight && `line-height: ${lineHeight}`};
  text-align: left;
  padding: 16px;
  border: 1px solid ${colors.gray.light};
  border-radius: 8px;
  -moz-border-radius: 8px;
  -webkit-border-radius: 8px;
  color: ${colors.gray.medium};
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  background-color: ${colors.white};
  display: block;
  outline: none;
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }

  &[disabled] {
    opacity: .5;
  }

  ${props => props.hasError && `
    border: 2px solid #C80E3B;
    color: #222222;
  `}
  ${({ icon }) => icon && `
    padding-left: 42px;
  `}
`

export const ErrorMessage = styled.p`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  padding-left: 15px;
  margin: 0;
  color: #C80E3B;
  position: absolute;
  bottom: -16.5px;
  ${({ counter }) => counter && `
    bottom: -1px;
  `}
`

const Icon = styled.div`
  display: flex;
  position: absolute;
  top: 18px;
  left: 18px;
  max-width: 20px;
  max-height: 20px;
  margin: 0px;
  z-index: 1;
`

const Input = ({ id, className, label, type, name, icon, onClick, onChange, onFocus, value, isReadOnly, placeholder = ' ', mask, maskChar, pattern, onBlur, autoComplete, errorMessage, hookForm, disabled = false, maxWidth, height, title, max, counter, marginBottom, ...rest }, ref) => {
  const hasError = !!errorMessage
  const INITIAL_COUNTER_VALUE = '0'
  const inputProps = {
    id,
    className,
    name,
    onChange,
    icon,
    type,
    value,
    readOnly: isReadOnly,
    placeholder,
    onBlur,
    autoComplete,
    disabled,
    onClick,
    onFocus,
    ref,
    title,
    ...hookForm,
    ...rest
  }
  const input = mask
    ? (
      <ReactInputMask
        {...inputProps}
        mask={mask}
        maskChar={maskChar}
        pattern={pattern}
        alwaysShowMask
      >
        {inputProps => <InputStyled {...inputProps} height={height} hasError={hasError} />}
      </ReactInputMask>
    )
    : <InputStyled {...inputProps} hasError={hasError} maxLength={max} height={height} />

  return (
    <Container maxWidth={maxWidth} marginBottom={marginBottom} hasError={hasError}>
      {input}
      {icon && <Icon>{icon}</Icon>}
      {label && (<Label disabled={disabled}>{label}</Label>)}
      {(max && counter) && <Max>{value?.length || INITIAL_COUNTER_VALUE}/{max}</Max>}
      {hasError && (<ErrorMessage counter={counter}>{errorMessage}</ErrorMessage>)}
    </Container>
  )
}

export default forwardRef(Input)
